/**
 * OpenRouter Settings — API key + optional default model.
 *
 * The plaintext key is never fetched back from the backend once saved. The UI
 * masks the input as password type, and shows only the last 4 characters of
 * the stored key so the operator can confirm which key is active. Leaving
 * the field blank on save keeps the existing key untouched; typing anything
 * (including "clear") replaces it. To fully remove the key, use the Delete
 * button.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bot, Save, Trash2, PlugZap, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ModelPicker from "@/components/ModelPicker";

export default function OpenRouterSettings() {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [defaultModel, setDefaultModel] = useState("");
  const [soapModel, setSoapModel] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function refresh() {
    try {
      const r = await api.get("/settings/openrouter");
      setCfg(r.data);
      setDefaultModel(r.data.default_model || "");
      setSoapModel(r.data.soap_model || "");
    } catch (e) {
      toast.error("Failed to load OpenRouter settings", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }
  useEffect(() => { refresh(); }, []);

  async function save() {
    setSaving(true);
    setTestResult(null);
    try {
      const body = { default_model: defaultModel, soap_model: soapModel };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await api.put("/settings/openrouter", body);
      setCfg(r.data);
      setApiKey("");
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    setTestResult(null);
    try {
      const body = apiKey.trim() ? { api_key: apiKey.trim() } : {};
      const r = await api.post("/settings/openrouter/test-connection", body);
      setTestResult(r.data);
      if (r.data.ok) {
        toast.success(`Connected — ${r.data.model_count ?? 0} models available`);
      } else {
        toast.error("Test failed", { description: r.data.error });
      }
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || e.message });
      toast.error("Test failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setTesting(false);
    }
  }

  async function clearKey() {
    if (!window.confirm(
      "Remove the stored OpenRouter API key? Any future AI features that depend on it will stop working until you save a new one.",
    )) return;
    setDeleting(true);
    setTestResult(null);
    try {
      const r = await api.delete("/settings/openrouter");
      setCfg(r.data);
      setApiKey("");
      toast.success("Key removed");
    } catch (e) {
      toast.error("Delete failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card
      className="p-6 rounded-lg border border-zinc-200 shadow-none"
      data-testid="openrouter-settings"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <Bot className="h-3.5 w-3.5" /> OpenRouter · AI integration
          </div>
          <h3 className="mt-1 font-display text-xl tracking-tight text-zinc-950">
            Store your OpenRouter API key
          </h3>
          <p className="mt-1 text-xs text-zinc-600 max-w-xl leading-relaxed">
            Configures the AI plumbing for future features (transcript summaries, smart
            replies, etc.). Get your key from{" "}
            <a
              href="https://openrouter.ai/settings/keys"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-zinc-900"
            >
              openrouter.ai/settings/keys
            </a>
            . Keys start with <code className="text-2xs bg-zinc-100 px-1 rounded">sk-or-</code> and
            are stored encrypted at rest.
          </p>
        </div>
        {cfg?.has_key ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded">
            <CheckCircle2 className="h-3 w-3" /> Configured
          </span>
        ) : (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200 px-2 py-1 rounded">
            <AlertCircle className="h-3 w-3" /> Not configured
          </span>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            API key
          </Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={
              cfg?.has_key
                ? `Saved · ••••${cfg.key_last4 ?? "····"} · leave blank to keep`
                : "sk-or-..."
            }
            className="border-zinc-300 mt-1.5 font-mono-num text-xs"
            data-testid="openrouter-api-key-input"
            autoComplete="off"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Default model (letters)
          </Label>
          <ModelPicker
            value={defaultModel}
            onChange={setDefaultModel}
            placeholder="e.g. moonshotai/kimi-k2"
            testId="openrouter-default-model-picker"
          />
          <p className="mt-1 text-2xs text-zinc-500">
            Used by the Letter Writer. Falls back to <code>moonshotai/kimi-k2</code> if blank.
          </p>
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            SOAP notes model
          </Label>
          <ModelPicker
            value={soapModel}
            onChange={setSoapModel}
            placeholder="e.g. moonshotai/kimi-k2"
            testId="openrouter-soap-model-picker"
          />
          <p className="mt-1 text-2xs text-zinc-500">
            Used by the assessment-recording pipeline to turn transcripts into
            structured SOAP notes. Falls back to <code>moonshotai/kimi-k2</code> if blank.
            If OpenRouter is unreachable, the system silently reverts to the
            Emergent Claude model and emails you.
          </p>
        </div>
      </div>

      {testResult && (
        <div
          data-testid="openrouter-test-result"
          className={cn(
            "mt-4 p-3 rounded-md border text-sm",
            testResult.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-700",
          )}
        >
          {testResult.ok ? (
            <span>
              Connection OK · {testResult.model_count ?? 0} models available on your account.
            </span>
          ) : (
            <span>Test failed · {testResult.error}</span>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2 border-t border-zinc-100 pt-5">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="openrouter-save-btn"
        >
          <Save className={cn("h-4 w-4 mr-1.5", saving && "animate-pulse")} />
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          variant="outline"
          onClick={test}
          disabled={testing || (!cfg?.has_key && !apiKey.trim())}
          className="border-zinc-300 hover:bg-zinc-100"
          data-testid="openrouter-test-btn"
          title={
            !cfg?.has_key && !apiKey.trim()
              ? "Enter a key or save one first"
              : "Send a cheap `/models` request to validate the key"
          }
        >
          <PlugZap className={cn("h-4 w-4 mr-1.5", testing && "animate-pulse")} />
          {testing ? "Testing…" : "Test connection"}
        </Button>
        {cfg?.has_key && (
          <Button
            variant="outline"
            onClick={clearKey}
            disabled={deleting}
            className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 ml-auto"
            data-testid="openrouter-delete-btn"
          >
            <Trash2 className={cn("h-4 w-4 mr-1.5", deleting && "animate-pulse")} />
            {deleting ? "Removing…" : "Remove key"}
          </Button>
        )}
      </div>

      {cfg?.updated_at && (
        <p className="mt-3 text-2xs text-zinc-400 font-mono-num">
          Last saved {cfg.updated_at.slice(0, 16).replace("T", " ")}
        </p>
      )}
    </Card>
  );
}
